/**
 * useTheme — single source of truth for the app-wide theme toggle.
 *
 * Theme state lives in a MODULE-LEVEL store shared by every component
 * (AppHeader, DashboardHeader, HeroCard, …) via useSyncExternalStore.
 * Previously each component held its own useState copy, so two mounted
 * toggles could desync and the first click appeared "swallowed".
 *
 * Reads/writes `localStorage["worthify_theme"]` and reflects the choice
 * onto `<html data-theme="…">`. Dark is the default.
 */
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "worthify_theme";
const DEFAULT_THEME = "dark";

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  // Keep meta theme-color in sync for mobile browser chrome
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "light" ? "#FFFFFF" : "#000000");
};

let currentTheme = typeof window === "undefined" ? DEFAULT_THEME : readStored();
const listeners = new Set();

const subscribe = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getSnapshot = () => currentTheme;

const setGlobalTheme = (theme) => {
  if (theme !== "light" && theme !== "dark") return;
  currentTheme = theme;
  applyTheme(theme);
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  listeners.forEach((cb) => cb());
};

export const useTheme = () => {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const toggleTheme = useCallback(() => {
    setGlobalTheme(currentTheme === "light" ? "dark" : "light");
  }, []);
  return { theme, toggleTheme, setTheme: setGlobalTheme };
};

/**
 * Initialise the theme attribute as early as possible — call this from
 * the module top of `index.js` so the first paint already has the
 * correct attribute (avoids a one-frame flash of the wrong palette).
 */
export const bootstrapTheme = () => {
  if (typeof window === "undefined") return;
  applyTheme(currentTheme);
};
